import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'neon';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-emerald hover:bg-emerald-600 active:bg-emerald-700 text-slate-950 font-semibold shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:shadow-[0_0_20px_rgba(16,185,129,0.5)] border border-emerald-400/30',
  neon:
    'bg-accent-neon hover:bg-[#00c985] text-slate-950 font-semibold shadow-[0_0_20px_rgba(0,229,153,0.4)] hover:shadow-[0_0_25px_rgba(0,229,153,0.6)] border border-white/20',
  secondary:
    'bg-surface-hover hover:bg-slate-700/80 active:bg-slate-700 text-text-primary border border-white/10 hover:border-white/20',
  outline:
    'bg-transparent hover:bg-surface-hover/50 text-text-primary border border-slate-600/60 hover:border-accent-emerald/60 hover:text-accent-emerald',
  ghost:
    'bg-transparent hover:bg-surface-hover/50 text-text-secondary hover:text-text-primary border border-transparent',
  danger:
    'bg-rose-500/20 hover:bg-rose-500/30 active:bg-rose-500/40 text-rose-300 border border-rose-500/40 hover:border-rose-500/60',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-lg min-h-[36px] gap-1.5',
  md: 'px-4 py-2 text-sm rounded-xl min-h-[44px] sm:min-h-[40px] gap-2',
  lg: 'px-6 py-3 text-base rounded-xl min-h-[48px] gap-2.5 font-medium',
  icon: 'w-11 h-11 min-h-[44px] min-w-[44px] p-2.5 rounded-xl justify-center items-center',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      fullWidth = false,
      leftIcon,
      rightIcon,
      loading = false,
      disabled = false,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`
          inline-flex items-center justify-center font-medium
          transition-all duration-200 select-none cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-accent-emerald/50 focus:ring-offset-2 focus:ring-offset-background
          disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none active:scale-[0.98]
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        {...props}
      >
        {loading ? (
          <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
        ) : leftIcon ? (
          <span className="flex-shrink-0">{leftIcon}</span>
        ) : null}
        {children && <span>{children}</span>}
        {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
